'use client';

import { PageShell } from '@/components/layout/page-shell';
import { useSearchExpansion } from '@/components/search/hooks/use-search-expansion';
import { SubscriptionModal } from '@/features/freemium/components/subscription-modal';
import { useGuestGate } from '@/features/freemium/hooks/use-guest-gate';
import { cn } from '@/shared/utils/formatting';
import { getCreatorRoute } from '@/shared/utils/routes';
import { Search, X } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSelectedLayoutSegments } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreatorTabsProps, TabConfig, TabType } from './types';

const TAB_ROUTES: Record<TabType, string> = {
  home: '',
  videos: 'videos',
  shorts: 'shorts',
  series: 'series',
  posts: 'posts',
  education: 'education',
  search: 'search',
};

/**
 * Tabs always blocked for guests regardless of content — their list
 * endpoints sit behind auth:sanctum + subscription, so a guest tap must
 * gate, never navigate. Counts (public payload) only decide visibility.
 */
const GUEST_BLOCKED_TABS = new Set<string>(['posts', 'series']);

function getActiveTab(segment: string | null): TabType {
  if (!segment) return 'home';
  const entry = Object.entries(TAB_ROUTES).find(([, route]) => route === segment);
  return (entry?.[0] as TabType) || 'home';
}

/**
 * Custom profile tabs route to nested `t/{uuid}` (two segments), so active
 * state matches the full segment chain; built-ins match the first segment
 * against TAB_ROUTES.
 */
function isTabActive(tab: TabConfig, segments: string[]): boolean {
  if (tab.segments) {
    return (
      tab.segments.length === segments.length && tab.segments.every((s, i) => segments[i] === s)
    );
  }
  const route = TAB_ROUTES[tab.key as TabType] ?? '';
  return route === (segments[0] ?? '');
}

export function CreatorTabs({
  handler,
  tabs,
  isGuest,
  creatorName,
  creatorAvatarSrc,
  peekVideos,
  totalLockedCount,
}: CreatorTabsProps) {
  const segments = useSelectedLayoutSegments();
  const activeTab = getActiveTab(segments[0] ?? null);
  const router = useRouter();
  const { gate, modalContext, closeModal } = useGuestGate();

  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const { isExpanded, expand, collapse, inputRef } = useSearchExpansion();

  const handleCollapse = useCallback(() => {
    collapse();
    setQuery('');
  }, [collapse]);

  // Click outside to collapse
  useEffect(() => {
    if (!isExpanded) return;

    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleCollapse();
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isExpanded, handleCollapse]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      handleCollapse();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = query.trim();
      if (trimmed) {
        router.push(`${getCreatorRoute(handler)}/search?q=${encodeURIComponent(trimmed)}`);
      } else {
        router.push(`${getCreatorRoute(handler)}/search`);
      }
    }
  }

  // Auto-collapse search when navigating away from search tab
  useEffect(() => {
    if (activeTab !== 'search') {
      handleCollapse();
    }
  }, [activeTab, handleCollapse]);

  const isSearchActive = isExpanded || activeTab === 'search';

  return (
    <section className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/10">
      <PageShell
        spacing="none"
        className="flex gap-5 sm:gap-[34px] overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {tabs.map((tab) => {
          const hasContent = tab.key === 'home' || (tab.count !== undefined && tab.count > 0);
          const isLocked = isGuest && (GUEST_BLOCKED_TABS.has(tab.key) || !hasContent);
          const base = getCreatorRoute(handler);
          const builtinRoute = TAB_ROUTES[tab.key as TabType];
          const href = tab.href ?? (builtinRoute ? `${base}/${builtinRoute}` : base);
          const active = isTabActive(tab, segments);

          if (isLocked) {
            return (
              <button
                key={tab.key}
                type="button"
                onClick={(e) =>
                  gate({
                    type: 'locked-tab',
                    tabName: tab.label,
                    creatorName: creatorName || '',
                    anchorEl: e.currentTarget,
                    ...(creatorAvatarSrc !== undefined ? { creatorAvatarSrc } : {}),
                    ...(peekVideos !== undefined ? { peekVideos } : {}),
                    ...(totalLockedCount !== undefined ? { totalLockedCount } : {}),
                  })
                }
                className={cn(
                  'bg-transparent border-none shrink-0',
                  'text-sm font-medium',
                  'px-3 py-3',
                  'border-b-2 border-transparent',
                  'transition-all duration-200',
                  'text-white/50 hover:text-white/80',
                  'cursor-pointer inline-flex items-center gap-2'
                )}
              >
                <span
                  aria-hidden="true"
                  className="inline-block w-1.5 h-1.5 rounded-full bg-red-primary shadow-[0_0_6px_rgba(204,34,34,0.8)]"
                />
                {tab.icon ? (
                  <span aria-hidden="true" className="text-sm leading-none">
                    {tab.icon}
                  </span>
                ) : null}
                <span>{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="text-xs opacity-70">({tab.count})</span>
                )}
              </button>
            );
          }

          return (
            <Link
              key={tab.key}
              href={href}
              className={cn(
                'bg-transparent border-none shrink-0 inline-flex items-center gap-[9px] whitespace-nowrap',
                'text-base',
                'px-px py-4',
                'border-b-2',
                'transition-all duration-200',
                'hover:text-white/70',
                active
                  ? 'text-white font-semibold border-red-primary'
                  : 'text-white/45 font-medium border-transparent',
                isExpanded && 'hidden sm:flex'
              )}
              {...(active && tab.color ? { style: { borderColor: tab.color } } : {})}
            >
              {tab.icon ? (
                <span
                  aria-hidden="true"
                  className={cn('text-base leading-none', isExpanded && 'hidden sm:inline')}
                  {...(tab.isCustom && tab.color ? { style: { color: tab.color } } : {})}
                >
                  {tab.icon}
                </span>
              ) : tab.isCustom ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-block size-[7px] shrink-0 rounded-full',
                    isExpanded && 'hidden sm:inline-block'
                  )}
                  style={{ background: tab.color ?? 'var(--color-red-primary)' }}
                />
              ) : null}
              <span className={cn(isExpanded && 'hidden sm:inline')}>{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && (
                <span className={cn('text-[14px] text-white/30', isExpanded && 'hidden sm:inline')}>
                  ({tab.count}
                  {tab.countLabel ? ` ${tab.countLabel}` : ''})
                </span>
              )}
            </Link>
          );
        })}

        {/* Inline expandable search — hidden for guests */}
        {!isGuest && (
          <div ref={containerRef} className="flex items-center shrink-0">
            <div
              className={cn(
                'flex items-center overflow-hidden transition-all duration-300',
                isExpanded ? 'w-48 sm:w-64 opacity-100' : 'w-0 opacity-0'
              )}
            >
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="w-full bg-white/10 text-white text-sm placeholder:text-white/40 px-3 py-2 rounded border border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-primary/30 focus-visible:border-red-primary"
              />
            </div>
            <button
              onClick={isExpanded ? handleCollapse : expand}
              className={cn(
                'flex items-center justify-center shrink-0',
                'px-3 py-2',
                'border-b-2',
                'transition-all duration-200',
                'hover:text-white/80',
                isSearchActive
                  ? 'text-white border-red-primary'
                  : 'text-white/50 border-transparent'
              )}
              aria-label={isExpanded ? 'Close search' : 'Search creator content'}
            >
              {isExpanded ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
            </button>
          </div>
        )}
      </PageShell>

      <SubscriptionModal
        open={!!modalContext}
        onOpenChange={() => closeModal()}
        context={modalContext}
      />
    </section>
  );
}
